import type { ExpertProfileCreateDto, ExpertProfileEducationDto } from '@@types/api.types';
import { ExpertProfileType } from '@@types/api.types';
import {
  TITLE_MAX_LENGTH,
  TITLE_MIN_LENGTH,
  TITLE_VALIDATION_MESSAGES,
} from '@azymmetric/shared/validation/validation.constants';
import { zArrayWithoutEmptyObjectSchema, zCoercedArrayWithoutEmptyValues, zCoercedOptional } from '@utils/zod.util';
import { z } from 'zod';

const expertProfileEducationSchema = z
  .object({
    endYear: z.number().optional(),
    institute: z.string().min(1, { message: 'Please enter the name of your educational institution' }),
    startYear: z.number().optional(),
    title: z
      .string()
      .min(TITLE_MIN_LENGTH, { message: TITLE_VALIDATION_MESSAGES.min })
      .max(TITLE_MAX_LENGTH, { message: TITLE_VALIDATION_MESSAGES.max }),
  })
  .strip()
  .superRefine((data, ctx) => {
    if (data.startYear && data.endYear && data.startYear > data.endYear) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Start year must be before or equal to end year',
        path: ['startYear'],
      });
    }
  }) satisfies z.ZodType<ExpertProfileEducationDto>;

export const expertProfileCreateSchema = z
  .object({
    capabilities: z.array(z.string().uuid()).optional(),
    categories: z.array(z.string().uuid()).optional(),
    cityOfResidence: zCoercedOptional(z.string().optional()),
    communities: z.array(z.string().uuid()).optional(),
    countryOfResidence: zCoercedOptional(z.string().optional()),
    countryOfService: zCoercedOptional(z.string().optional()),
    domains: z.array(z.string().uuid()).optional(),
    educations: zArrayWithoutEmptyObjectSchema(expertProfileEducationSchema).optional(),
    effects: z.array(z.string().uuid()).optional(),
    linkedinUrl: zCoercedOptional(
      z.string().url({ message: 'Please enter a valid LinkedIn profile address' }).optional(),
    ),
    mrlAssessmentRange: zCoercedArrayWithoutEmptyValues(z.array(z.number()).optional()),
    phases: zCoercedArrayWithoutEmptyValues(z.array(z.number()).optional()),
    serviceId: z.string().uuid().optional(),
    stateOfResidence: zCoercedOptional(z.string().optional()),
    trlAssessmentRange: zCoercedArrayWithoutEmptyValues(z.array(z.number()).optional()),
    type: z.nativeEnum(ExpertProfileType),
    userId: z.string().uuid(),
    websiteUrl: zCoercedOptional(z.string().url({ message: 'Please enter a valid website address' }).optional()),
    yearsOfExperience: zCoercedOptional(
      z.coerce.number().positive({ message: 'Years of experience must be a positive number' }).optional(),
    ),
  })
  .strip() satisfies z.ZodType<ExpertProfileCreateDto>;

export const typeStepSchema = expertProfileCreateSchema.pick({
  type: true,
});

export const profileStepSchema = expertProfileCreateSchema.pick({
  linkedinUrl: true,
  websiteUrl: true,
  countryOfResidence: true,
  stateOfResidence: true,
  cityOfResidence: true,
  countryOfService: true,
  serviceId: true,
});

export const backgroundStepSchema = expertProfileCreateSchema.pick({
  communities: true,
  yearsOfExperience: true,
  educations: true,
});

export const expertiseStepSchema = expertProfileCreateSchema
  .pick({
    trlAssessmentRange: true,
    mrlAssessmentRange: true,
    categories: true,
    domains: true,
    capabilities: true,
    effects: true,
    phases: true,
  })
  .superRefine((data, ctx) => {
    if (data.phases && data.phases.length > 0) {
      const [phase1, phase2] = data.phases;
      if ((phase1 === undefined && phase2 !== undefined) || (phase1 !== undefined && phase2 === undefined)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please select both minimum and maximum phase values',
          path: ['phases'],
        });
      }
    }
    if (data.trlAssessmentRange && data.trlAssessmentRange.length > 0) {
      const [minTrl, maxTrl] = data.trlAssessmentRange;
      if ((minTrl === undefined && maxTrl !== undefined) || (minTrl !== undefined && maxTrl === undefined)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please select both minimum and maximum TRL values',
          path: ['trlAssessmentRange'],
        });
      } else if (minTrl !== undefined && maxTrl !== undefined && minTrl > maxTrl) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Minimum TRL value must be less than or equal to maximum TRL value',
          path: ['trlAssessmentRange'],
        });
      }
    }
    if (data.mrlAssessmentRange && data.mrlAssessmentRange.length > 0) {
      const [minMrl, maxMrl] = data.mrlAssessmentRange;
      if ((minMrl === undefined && maxMrl !== undefined) || (minMrl !== undefined && maxMrl === undefined)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please select both minimum and maximum MRL values',
          path: ['mrlAssessmentRange'],
        });
      } else if (minMrl !== undefined && maxMrl !== undefined && minMrl > maxMrl) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Minimum MRL value must be less than or equal to maximum MRL value',
          path: ['mrlAssessmentRange'],
        });
      }
    }
  });
